import { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";

import { authStore, type AuthSessionSnapshot } from "../features/auth/authStore";
import { readActorUserIDFromAccessToken } from "../features/chats/chatApi";
import { LocaleProvider } from "../features/i18n/LocaleProvider";
import {
  NOTIFICATION_RUNTIME_REFRESH_EVENT_TYPE,
  readNotificationPermissionState,
  readNotificationRuntimeInstallState,
  requestNotificationPermissionFromUserGesture,
  startNotificationRuntime,
} from "../features/notifications/notificationClient";
import {
  NotificationInstallHint,
  NotificationPermissionPrompt,
  shouldShowNotificationInstallHint,
  shouldShowNotificationPermissionPrompt,
} from "../features/notifications/NotificationInstallHint";
import { ThemeProvider } from "../features/theme/ThemeProvider";
import { appRouter } from "./router";

function readAuthenticatedNotificationSession(snapshot: AuthSessionSnapshot): {
  actorUserID: string;
  accessToken: string;
} | null {
  const accessToken = snapshot.accessToken;
  if (accessToken === null) {
    return null;
  }
  const actorUserID = readActorUserIDFromAccessToken(accessToken);
  if (actorUserID === null) {
    return null;
  }
  return {
    actorUserID,
    accessToken,
  };
}

function NotificationRuntimeBridge() {
  useEffect(() => {
    let stopRuntime: (() => void) | null = null;

    const refreshRuntime = () => {
      if (stopRuntime !== null) {
        stopRuntime();
        stopRuntime = null;
      }

      const snapshot = authStore.getSnapshot();
      const activeSession = readAuthenticatedNotificationSession(snapshot);
      if (activeSession === null) {
        return;
      }

      stopRuntime = startNotificationRuntime({
        actorUserID: activeSession.actorUserID,
        accessToken: activeSession.accessToken,
      });
    };

    refreshRuntime();
    const unsubscribe = authStore.subscribe(() => {
      refreshRuntime();
    });

    return () => {
      unsubscribe();
      if (stopRuntime !== null) {
        stopRuntime();
      }
    };
  }, []);

  return null;
}

function NotificationInstallHintBridge() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const refreshHintVisibility = () => {
      const snapshot = authStore.getSnapshot();
      const installState = readNotificationRuntimeInstallState();
      const session = readAuthenticatedNotificationSession(snapshot);
      setOpen(
        shouldShowNotificationInstallHint({
          hasAuthenticatedSession: session !== null,
          installState,
        }),
      );
    };

    refreshHintVisibility();
    const unsubscribe = authStore.subscribe(() => {
      refreshHintVisibility();
    });

    if (typeof window === "undefined" || typeof document === "undefined") {
      return () => {
        unsubscribe();
      };
    }

    const displayModeQuery =
      typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)") : null;
    const onRuntimeContextChange = () => {
      refreshHintVisibility();
    };

    window.addEventListener("focus", onRuntimeContextChange);
    document.addEventListener("visibilitychange", onRuntimeContextChange);

    if (displayModeQuery !== null) {
      if (typeof displayModeQuery.addEventListener === "function") {
        displayModeQuery.addEventListener("change", onRuntimeContextChange);
      } else if (typeof displayModeQuery.addListener === "function") {
        displayModeQuery.addListener(onRuntimeContextChange);
      }
    }

    return () => {
      unsubscribe();
      window.removeEventListener("focus", onRuntimeContextChange);
      document.removeEventListener("visibilitychange", onRuntimeContextChange);

      if (displayModeQuery !== null) {
        if (typeof displayModeQuery.removeEventListener === "function") {
          displayModeQuery.removeEventListener("change", onRuntimeContextChange);
        } else if (typeof displayModeQuery.removeListener === "function") {
          displayModeQuery.removeListener(onRuntimeContextChange);
        }
      }
    };
  }, []);

  return <NotificationInstallHint open={open} />;
}

function NotificationPermissionPromptBridge() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const refreshPromptVisibility = () => {
      const snapshot = authStore.getSnapshot();
      const session = readAuthenticatedNotificationSession(snapshot);
      const permissionState = readNotificationPermissionState();
      setOpen(
        shouldShowNotificationPermissionPrompt({
          hasAuthenticatedSession: session !== null,
          supported: permissionState.supported,
          permission: permissionState.permission,
        }),
      );
    };

    refreshPromptVisibility();
    const unsubscribe = authStore.subscribe(() => {
      refreshPromptVisibility();
    });

    if (typeof window === "undefined" || typeof document === "undefined") {
      return () => {
        unsubscribe();
      };
    }

    const onRuntimeContextChange = () => {
      refreshPromptVisibility();
    };
    window.addEventListener("focus", onRuntimeContextChange);
    window.addEventListener(NOTIFICATION_RUNTIME_REFRESH_EVENT_TYPE, onRuntimeContextChange);
    document.addEventListener("visibilitychange", onRuntimeContextChange);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", onRuntimeContextChange);
      window.removeEventListener(NOTIFICATION_RUNTIME_REFRESH_EVENT_TYPE, onRuntimeContextChange);
      document.removeEventListener("visibilitychange", onRuntimeContextChange);
    };
  }, []);

  const onEnable = () => {
    setBusy(true);
    void requestNotificationPermissionFromUserGesture()
      .catch(() => "default" as NotificationPermission)
      .then(() => {
        const permissionState = readNotificationPermissionState();
        setOpen(
          shouldShowNotificationPermissionPrompt({
            hasAuthenticatedSession: readAuthenticatedNotificationSession(authStore.getSnapshot()) !== null,
            supported: permissionState.supported,
            permission: permissionState.permission,
          }),
        );
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return <NotificationPermissionPrompt open={open} busy={busy} onEnable={onEnable} />;
}

export function AppProviders() {
  return (
    <ThemeProvider>
      <LocaleProvider>
        <NotificationInstallHintBridge />
        <NotificationPermissionPromptBridge />
        <NotificationRuntimeBridge />
        <RouterProvider
          router={appRouter}
          future={{ v7_startTransition: true }}
          fallbackElement={<div>Loading app shell...</div>}
        />
      </LocaleProvider>
    </ThemeProvider>
  );
}
